/**
 * Realtime patches for the site entity's normalized stores.
 *
 * The odl-app socket events fan into the entity stores the same way the
 * retrieve does: `site`, `unit`, `alarms`, and `command_activity` replace their
 * normalized state; `upgrade_progress` merges firmware progress into the unit.
 * Entity handlers drop pre-seed and out-of-order events. Command activity is
 * accepted before the site seed so a command committed inside the initial
 * fetch→subscribe window cannot be overwritten by that stale REST response. A
 * clear-alarm call optimistically drops its row ahead of the authoritative replace.
 *
 * Contains:
 * - site, unit, alarms, command activity, and upgrade-progress handlers
 * - removeAlarmFromSnapshot — optimistic single-alarm removal
 */
import { COMMAND_ACTIVITY_SOURCE, replaceCommandActivity, resolveCommandActivitySocketUpdate } from './siteCommands';
import { removeAlarm, replaceAlarms } from './sliceAlarms';
import { replaceSite } from './sliceSite';
import { mergeUpgradeProgress, replaceUnit } from './sliceUnit';

// A site is "seeded" once its REST retrieve has populated the stores; socket
// events before that are dropped (the retrieve will carry the latest state).
const isSeeded = (state, key) => state.site.bySiteId[key] != null;

/**
 * Socket handler for the v2 dashboard 'site' model event (full replace).
 *
 * @param {'+' | '-'} _op  - unused (site is always a full replace)
 * @param {object} payload - the site contract object (entity data only)
 * @param {{ siteId: number|string, generatedAt: ?string }} meta - envelope metadata
 */
export const handleSiteDashboardSiteSocketActions = (_op, payload, meta) => (dispatch, getState) => {
  const siteId = meta?.siteId;
  if (!payload || siteId == null) return;
  const key = String(siteId);
  if (!isSeeded(getState(), key)) return;
  dispatch(replaceSite({ siteId: key, site: payload }));
};

/**
 * Socket handler for the 'unit' model event (full replace).
 *
 * Guards: pre-seed no-op, monotonic generated_at (older than stored → drop).
 * generated_at is envelope metadata, persisted in the slice alongside the unit
 * (never on the unit object itself).
 *
 * @param {'+' | '-'} _op  - unused (unit is always a full replace)
 * @param {object} payload - the unit contract object (entity data only)
 * @param {{ siteId: number|string, generatedAt: ?string }} meta - envelope metadata
 */
export const handleUnitSocketActions = (_op, payload, meta) => (dispatch, getState) => {
  const siteId = meta?.siteId;
  if (!payload || siteId == null) return;
  const key = String(siteId);
  const state = getState();
  if (!isSeeded(state, key)) return;

  // Monotonic guard: don't overwrite a newer unit with an older socket emit.
  const existingGeneratedAt = state.siteUnit.generatedAtBySiteId[key];
  const incomingGeneratedAt = meta?.generatedAt;
  if (
    existingGeneratedAt != null &&
    incomingGeneratedAt != null &&
    new Date(incomingGeneratedAt) < new Date(existingGeneratedAt)
  ) {
    return;
  }

  dispatch(
    replaceUnit({
      siteId: key,
      unit: payload,
      generatedAt: incomingGeneratedAt ?? null,
      source: COMMAND_ACTIVITY_SOURCE.SOCKET,
      snapshotRequestVersion: state.siteUnit.latestSnapshotRequestVersionBySiteId[key] ?? 0,
    }),
  );
};

/**
 * Socket handler for the 'alarms' model event (full replace list).
 *
 * Same guard pattern as the unit handler; the monotonic timestamp comes from the
 * envelope (meta) and is stored on the `generatedAt` sibling so an optimistic
 * removeAlarm doesn't reset it and let an out-of-order emit resurrect a cleared
 * alarm. On the socket path the server ships all alarm groups including
 * is_admin_only ones; non-admin users filter client-side (view reads
 * capabilities.is_staff).
 *
 * @param {'+' | '-'} _op  - unused (alarms is always a full replace list)
 * @param {object[]} payload - the alarms contract list (entity data only)
 * @param {{ siteId: number|string, generatedAt: ?string }} meta - envelope metadata
 */
export const handleAlarmsSocketActions = (_op, payload, meta) => (dispatch, getState) => {
  const siteId = meta?.siteId;
  if (!payload || siteId == null) return;
  const key = String(siteId);
  const state = getState();
  if (!isSeeded(state, key)) return;

  const existingGeneratedAt = state.siteAlarms.bySiteId[key]?.generatedAt;
  const incomingGeneratedAt = meta?.generatedAt;
  if (
    existingGeneratedAt != null &&
    incomingGeneratedAt != null &&
    new Date(incomingGeneratedAt) < new Date(existingGeneratedAt)
  ) {
    return;
  }

  // The alarms contract delivers a bare list; tolerate an {alarms:[...]} wrapper.
  const list = Array.isArray(payload) ? payload : (payload.alarms ?? []);
  dispatch(replaceAlarms({ siteId: key, list, generatedAt: incomingGeneratedAt ?? null }));
};

/** Full-replace command activity before or after REST seed, dropping older emits. */
export const handleCommandActivitySocketActions = (_op, payload, meta) => (dispatch, getState) => {
  const siteId = meta?.siteId;
  if (siteId == null) return;

  const key = String(siteId);
  const state = getState();
  const update = resolveCommandActivitySocketUpdate(
    payload,
    { ...meta, siteId: key },
    {
      lastGeneratedAt: state.siteCommands?.bySiteId?.[key]?.generatedAt ?? null,
    },
  );
  if (update) {
    dispatch(
      replaceCommandActivity({
        ...update,
        source: COMMAND_ACTIVITY_SOURCE.SOCKET,
        requestVersion: state.siteUnit.latestSnapshotRequestVersionBySiteId[key] ?? 0,
      }),
    );
  }
};

/**
 * Socket handler for the lightweight 'upgrade_progress' event.
 *
 * Merges only programming_segment, end_segment, and batch_id into the unit's
 * current_upgrade. Dropped when the site is not seeded or the unit has no
 * active upgrade yet (the full `unit` event at upgrade start seeds
 * current_upgrade, after which progress events are safe).
 *
 * @param {'+' | '-'} _op  - unused
 * @param {object} payload - { batch_id, programming_segment, end_segment }
 * @param {{ siteId: number|string, generatedAt: ?string }} meta - envelope metadata
 */
export const handleUpgradeProgressContractSocketActions = (_op, payload, meta) => (dispatch, getState) => {
  const siteId = meta?.siteId;
  if (!payload || siteId == null) return;
  const key = String(siteId);
  const state = getState();
  if (!isSeeded(state, key)) return;
  if (state.siteUnit.bySiteId[key]?.current_upgrade == null) return;

  const { programming_segment, end_segment, batch_id } = payload;
  dispatch(mergeUpgradeProgress({ siteId: key, programming_segment, end_segment, batch_id }));
};

/**
 * Optimistically drop one alarm state row immediately after a clear call
 * succeeds, before the authoritative socket 'alarms' replace arrives.
 *
 * @param {number|string} siteId
 * @param {number|string} alarmStateId - the alarm state `id` field
 */
export const removeAlarmFromSnapshot = (siteId, alarmStateId) => dispatch =>
  dispatch(removeAlarm({ siteId, alarmStateId }));
